// =============================================================================
// FolderSecurityModal.jsx — KlassDoc Sprint 10C H10 Part B2
// -----------------------------------------------------------------------------
// "Gérer la sécurité du dossier" / "Manage Folder Security" dialogue.
//
// PURPOSE
// Opens from the "Manage Security" button on folder cards for users with
// Full Access or admin role. Only valid on TOP-LEVEL folders — subfolders
// inherit from the top-level ancestor under ADR-008 Path 2.
//
// FLOW
//   1. Parent renders <FolderSecurityModal folder={top-level folder} ... />
//   2. Modal calls GET /api/permissions/folder/:id — backend rejects with
//      code: 'NOT_TOP_LEVEL' if the caller passed a subfolder.
//   3. User adds/removes/edits principals.
//   4. User clicks "Save" → POST /api/permissions/folder/:id
//   5. Modal calls onSuccess() and closes.
//
// PROPS
//   folder       : Top-level folder { id, name }
//   onClose      : Callback when user cancels or closes
//   onSuccess    : Callback after successful save
//   api          : Shared ApiClient instance
//   currentUser  : Logged-in user object
//   lang         : "fr" or "en"
//   THEME        : Shared theme object
//
// NOTES
// - Creator of the folder and the Administrators group row are "locked"
//   (same convention as the doc security modal — they can't be removed
//   but their access level can be changed). Admin-group lock avoids the
//   admins-locking-themselves-out footgun.
// - Reuses the Sprint 6.5 SecurityControls primitives (PrincipalPicker,
//   AclEntryRow, AccessLevelDropdown) verbatim — no new UI components.
// =============================================================================

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import * as lucide from "lucide-react";
import { PrincipalPicker, AclEntryRow } from "./security/SecurityControls";

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function FolderSecurityModal({ folder, onClose, onSuccess, api, currentUser, lang = "fr", THEME }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aclEntries, setAclEntries] = useState([]);
  const [adminGroupId, setAdminGroupId] = useState(null);

  useEffect(() => {
    if (!folder?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/permissions/folder/${folder.id}`),
      api.get("/groups"),
    ]).then(([permRes, groupsRes]) => {
      const admins = (groupsRes.data || []).find(g => g.name === "administrators");
      const adminId = admins?.id || null;
      setAdminGroupId(adminId);

      // Mark creator (granted_by = creator id when safety-net fired) and
      // admin group as locked. We don't reliably know the creator on the
      // frontend, so we key the "locked" flag off whether an entry is
      // the Administrators group OR is a user-grant whose principal_id
      // matches folder.created_by_id if the backend sent it.
      const entries = (permRes.data || []).map(e => {
        const isAdminGroup = e.principal_type === "group" && e.principal_id === adminId;
        const isCreator = e.principal_type === "user" && folder.created_by_id && e.principal_id === folder.created_by_id;
        return {
          principal_type: e.principal_type,
          principal_id: e.principal_id,
          principal_name: e.principal_name,
          access_level: e.access_level,
          locked: isAdminGroup || isCreator,
        };
      });
      setAclEntries(entries);
      setLoading(false);
    }).catch(e => {
      console.error("Load folder ACL error:", e);
      // Surface the NOT_TOP_LEVEL code explicitly so the caller knows
      // to redirect, though the UI should never open this modal on a
      // subfolder in the first place.
      const msg = e?.code === 'NOT_TOP_LEVEL'
        ? fmt(lang, "Les permissions sont gérées au niveau du dossier parent.", "Permissions are managed on the top-level folder.")
        : fmt(lang, "Échec du chargement des permissions", "Failed to load permissions");
      setError(msg);
      setLoading(false);
    });
  }, [folder?.id, folder?.created_by_id, api, lang]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !saving) onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  function addPrincipal(p) { setAclEntries(prev => [...prev, p]); }
  function updatePrincipalAccess(idx, level) {
    setAclEntries(prev => prev.map((e, i) => i === idx ? { ...e, access_level: level } : e));
  }
  function removePrincipal(idx) {
    setAclEntries(prev => prev.filter((_, i) => i !== idx));
  }

  const excludeIds = new Set(aclEntries.map(e => `${e.principal_type}-${e.principal_id}`));

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const entries = aclEntries.filter(e => e.principal_id).map(e => ({
        principal_type: e.principal_type,
        principal_id: e.principal_id,
        principal_name: e.principal_name,
        access_level: e.access_level,
      }));
      await api.post(`/permissions/folder/${folder.id}`, { entries });
      onSuccess?.();
    } catch (e) {
      console.error("Save folder ACL error:", e);
      setError(e.message || fmt(lang, "Échec de l'enregistrement", "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
        style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}
      >
        {/* HEADER */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${THEME.warning}40` }}>
              <lucide.FolderLock size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>
                {fmt(lang, "Gérer la sécurité du dossier", "Manage Folder Security")}
              </div>
              <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 2 }}>
                {folder?.name}
              </div>
            </div>
          </div>
          <button onClick={() => !saving && onClose()} disabled={saving} style={{ background: "transparent", border: "none", cursor: saving ? "not-allowed" : "pointer", padding: 6, opacity: saving ? 0.4 : 1 }}>
            <lucide.X size={20} color={THEME.textMuted} />
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px" }}>
          {error && (
            <div style={{ padding: "12px 16px", marginBottom: 16, background: THEME.dangerDim, border: `1px solid ${THEME.danger}40`, borderRadius: 8, color: THEME.danger, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
              <lucide.AlertCircle size={16} />{error}
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, gap: 12 }}>
              <lucide.Loader2 size={24} color={THEME.accent} className="spin" />
              <div style={{ fontSize: 13, color: THEME.textDim }}>{fmt(lang, "Chargement des permissions...", "Loading permissions...")}</div>
            </div>
          ) : (
            <>
              {/* Info banner — Path 2 semantics */}
              <div style={{ padding: 14, marginBottom: 20, background: THEME.accentDim, border: `1px solid ${THEME.accent}40`, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: THEME.accent + "1A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <lucide.Info size={18} color={THEME.accent} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: THEME.text }}>
                    {fmt(lang, "Sécurité au niveau du dossier", "Folder-level security")}
                  </div>
                  <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 3, lineHeight: 1.5 }}>
                    {fmt(lang,
                      "Les permissions définies ici s'appliquent à tous les sous-dossiers et documents à l'intérieur. Les utilisateurs non listés n'auront aucun accès.",
                      "Permissions set here apply to all subfolders and documents inside. Users not listed below will have no access.")}
                  </div>
                </div>
              </div>

              {/* Add principal picker */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{fmt(lang, "Ajouter des utilisateurs/groupes", "Add Users/Groups")}</label>
                <PrincipalPicker api={api} lang={lang} THEME={THEME} onAdd={addPrincipal} excludeIds={excludeIds} />
              </div>

              {/* ACL list */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  {aclEntries.length} {fmt(lang, "principaux avec permissions", "principals with permissions")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {aclEntries.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: THEME.textDim, fontSize: 13 }}>
                      {fmt(lang, "Aucune permission définie — ce dossier est invisible aux non-administrateurs.", "No permissions defined — this folder is invisible to non-administrators.")}
                    </div>
                  ) : aclEntries.map((entry, idx) => (
                    <AclEntryRow
                      key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                      entry={entry}
                      onChangeAccess={lvl => updatePrincipalAccess(idx, lvl)}
                      onRemove={() => removePrincipal(idx)}
                      lang={lang}
                      THEME={THEME}
                      readOnly={false}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: THEME.surface }}>
          <div style={{ fontSize: 11, color: THEME.textDim, display: "flex", alignItems: "center", gap: 6 }}>
            <lucide.Lock size={11} />
            {fmt(lang, "Le groupe Administrateurs conserve toujours l'accès complet", "Administrators group always retains full access")}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onClose} disabled={saving} style={{ padding: "10px 20px", background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.4 : 1, fontFamily: FONT }}>
              {fmt(lang, "Annuler", "Cancel")}
            </button>
            <button type="button" onClick={handleSave} disabled={saving || loading} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (saving || loading) ? "wait" : "pointer", boxShadow: `0 4px 16px ${THEME.warning}40`, fontFamily: FONT, opacity: (saving || loading) ? 0.7 : 1 }}>
              {saving ? <lucide.Loader2 size={15} className="spin" /> : <lucide.Save size={15} />}
              {saving ? fmt(lang, "Enregistrement...", "Saving...") : fmt(lang, "Enregistrer", "Save")}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
